import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Lock, Crown, ArrowRight, Sparkles } from 'lucide-react';
import { DashboardLayout } from './DashboardLayout';
import { motion } from 'framer-motion';

export const SubscriptionWall = () => {
  const { isDark } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  const trial = user?.trial || {};
  const isTrialExpired = trial.trial_expired === true;
  const isTrialActive = trial.trial_active === true;

  const heading = isTrialExpired
    ? 'Your Trial Has Ended'
    : isTrialActive
    ? 'Feature Reserved for Subscribers'
    : 'Subscription Required';

  const subtext = isTrialExpired
    ? 'Your free trial period has expired. Subscribe now to continue using all PropertyShop features.'
    : isTrialActive
    ? 'This feature is available for subscribed users only. Upgrade your plan to unlock full access.'
    : 'You need an active subscription to access this feature. Choose a plan to get started.';

  return (
    <DashboardLayout>
      <div className="flex items-center justify-center min-h-[60vh]">
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.4, ease: 'easeOut' }}
          className="text-center max-w-md mx-auto px-6"
        >
          {/* Icon */}
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.15, type: 'spring', stiffness: 200 }}
            className={`w-20 h-20 rounded-2xl mx-auto mb-6 flex items-center justify-center ${
              isDark ? 'bg-[var(--brand)]/10' : 'bg-amber-50'
            }`}
          >
            <Lock className="w-10 h-10 text-[var(--brand)]" />
          </motion.div>

          {/* Heading */}
          <h1 className={`text-2xl sm:text-3xl font-serif font-bold mb-3 ${isDark ? 'text-white' : 'text-gray-900'}`} data-testid="subscription-wall-heading">
            {heading}
          </h1>

          {/* Subtext */}
          <p className={`text-sm sm:text-base mb-8 leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
            {subtext}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col gap-3">
            <Button
              onClick={() => navigate('/subscription')}
              className="w-full bg-[var(--brand)] hover:opacity-90 text-white font-semibold py-6 text-base"
              data-testid="subscription-wall-subscribe-btn"
            >
              <Crown className="w-5 h-5 mr-2" />
              View Subscription Plans
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className={`w-full py-5 ${isDark ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-700 hover:bg-gray-100'}`}
              data-testid="subscription-wall-back-btn"
            >
              Back to Dashboard
            </Button>
          </div>

          {/* Features teaser */}
          <div className={`mt-8 p-4 rounded-xl border ${isDark ? 'bg-white/5 border-white/10' : 'bg-gray-50 border-gray-200'}`}>
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="w-4 h-4 text-[var(--brand)]" />
              <span className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Unlock with a subscription</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              {['Unlimited Properties', 'AI Chat Assistant', 'Marketplace Listing', 'Share Cards', 'Maps Access', 'Analytics'].map((f, i) => (
                <div key={i} className={`flex items-center gap-1.5 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                  <div className="w-1.5 h-1.5 rounded-full bg-[var(--brand)]" />
                  {f}
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </DashboardLayout>
  );
};
